import type { PayloadAction } from '@reduxjs/toolkit';
import type { AriaAttributes } from 'react';

import { createSlice } from '@reduxjs/toolkit';

/* eslint-disable no-param-reassign */

interface CurrentPageState {
    title?: string;
    a11yTitle?: string;
    canonicalPathName?: string;
    gaPageTypeName?: string;
    /** announces the text on the page */
    a11yAnnouncement?: A11yAnnouncement;

    /**
     * Includes leading question mark
     */
    queryString?: string;
    mountCounter: number;
}

export interface A11yAnnouncement {
    text: string;
    /** for debug purpose, see who sent this event */
    sender: string;

    /** aria-live attribute */
    ariaLive?: AriaAttributes['aria-live'];
}

const initialState: CurrentPageState = {
    canonicalPathName: undefined,
    title: undefined,
    a11yTitle: undefined,
    mountCounter: 0,
    gaPageTypeName: undefined,
    queryString: undefined,
    a11yAnnouncement: undefined,
};

const slice = createSlice({
    name: 'currentPage',
    initialState,
    reducers: {
        setPageGaData: (
            state,
            action: PayloadAction<{
                title: string;
                canonicalUrl: string;
                gaPageTypeName?: string;
                queryString?: string;
            }>,
        ) => {
            const { pathname } = new URL(action.payload.canonicalUrl);

            state.title = action.payload.title;
            state.canonicalPathName = pathname;
            state.gaPageTypeName = action.payload.gaPageTypeName;
            state.queryString = action.payload.queryString;

            state.mountCounter += 1;
        },
        setTitle: (state, action: PayloadAction<string>) => {
            state.title = action.payload;
            state.a11yTitle = undefined; // When page changes, there shouldn't be any custom a11y left from the previous page.
        },
        setA11yTitle: (state, action: PayloadAction<string>) => {
            state.a11yTitle = action.payload;
        },
        setA11yAnnouncement: (state, action: PayloadAction<A11yAnnouncement | undefined>) => {
            state.a11yAnnouncement = action.payload;
        },
    },
});

export const currentPageActions = slice.actions;

export default slice.reducer;
